import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { resolveAsset } from "@/lib/assets";

const BrandCtx = createContext(null);

const DEFAULT = {
  app_name: "CRM Suite",
  tagline: "Multi-business CRM, real-time chat, and instant lead capture.",
  logo_url: "",
  favicon_url: "",
  primary_color: "#21095b",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  support_email: "",
  support_phone: "",
  footer_text: "© CRM Suite. Enterprise-grade.",
  login_headline: "",
  login_subtext: "",
  client_portal_name: "Client Portal",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---- Color helpers ----
function hexToRgb(hex) {
  if (!hex) return null;
  let c = hex.trim().replace("#", "");
  if (c.length === 3) c = c.split("").map((ch) => ch + ch).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(c)) return null;
  const num = parseInt(c, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}
function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
function hexToHslString(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  return `${h} ${s}% ${l}%`;
}
// Perceived brightness (YIQ). Returns true if color is dark.
function isDark(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const y = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return y < 128;
}
// Lighten a hex color by percentage (0-100). Positive lightens, negative darkens.
export function lightenHex(hex, pct = 20) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const t = pct / 100;
  const mix = (c) => Math.round(t > 0 ? c + (255 - c) * t : c + c * t);
  const r = mix(rgb.r), g = mix(rgb.g), b = mix(rgb.b);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    api.get("/system-config")
      .then((r) => setBrand({ ...DEFAULT, ...r.data }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply document.title & favicon whenever brand changes
  useEffect(() => {
    if (brand.app_name) document.title = brand.app_name;
    if (brand.favicon_url) {
      const href = resolveAsset(brand.favicon_url);
      // Some templates ship an <link rel="shortcut icon"> alongside <link rel="icon">.
      // Update every existing icon-link (and add one if none exist) so browsers pick up
      // the fresh favicon consistently and don't keep the stale bundled one cached.
      const existing = document.querySelectorAll("link[rel~='icon']");
      if (existing.length === 0) {
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = href;
        document.head.appendChild(link);
      } else {
        existing.forEach((el) => { el.href = href; });
      }
    }
  }, [brand.app_name, brand.favicon_url]);

  // Inject primary_color into CSS variables so the whole app (admin, client portal, login) re-themes
  useEffect(() => {
    const hex = brand.primary_color || DEFAULT.primary_color;
    const hsl = hexToHslString(hex);
    if (!hsl) return;
    const root = document.documentElement;
    root.style.setProperty("--accent", hsl);
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--ring", hsl);
    root.style.setProperty("--chart-1", hsl);
    // Contrast foreground for buttons/pills using accent as background
    const fg = isDark(hex) ? "0 0% 100%" : "240 10% 8%";
    root.style.setProperty("--accent-foreground", fg);
    root.style.setProperty("--primary-foreground", fg);
    // Also expose the hex for gradient / style={{ background }} consumers
    root.style.setProperty("--brand-color", hex);
    root.style.setProperty("--brand-color-light", lightenHex(hex, 25));
    root.style.setProperty("--brand-color-lighter", lightenHex(hex, 45));
  }, [brand.primary_color]);

  const save = async (patch) => {
    const { data } = await api.put("/system-config", patch);
    setBrand({ ...DEFAULT, ...data });
    return data;
  };

  const logoSrc = resolveAsset(brand.logo_url);

  return (
    <BrandCtx.Provider value={{ brand, loaded, load, save, logoSrc, lightenHex }}>
      {children}
    </BrandCtx.Provider>
  );
}

export const useBrand = () => useContext(BrandCtx);
